import { useCallback, useMemo, useState } from "react";
import { LocalDate } from "@js-joda/core";
import {
  addStockholmDays,
  buildDateWithTimeFromLocalDate,
  formatTotalTime,
  parseTimeValue,
  shouldRollToNextDay,
  toStockholmLocalDateFromUtcIso,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import { toTimeInputValue } from "web-apps/company/utils/timeAndDate";
import {
  coerceToNonNegativeDecimalString,
  normalizeNonNegativeDecimalNumber,
  sanitizeNonNegativeDecimalInput,
} from "utils/input.normalize.logic";

export interface ShiftDetailsEditDerived {
  startDate: Date;
  endDate: Date;
  breakStartDate: Date;
  breakEndDate: Date;
  breakMinutes: number;
}

export interface ShiftDetailsEditState {
  isEditing: boolean;
  editStartTime: string;
  editEndTime: string;
  editBreakStartTime: string;
  editBreakEndTime: string;
  editDeductionAmount: string;
  editDate: LocalDate | null;
  editDerived: ShiftDetailsEditDerived | null;
  editTotalTime: string | null;
  canSaveEdit: boolean;
  setEditStartTime: (value: string) => void;
  setEditEndTime: (value: string) => void;
  setEditBreakStartTime: (value: string) => void;
  setEditBreakEndTime: (value: string) => void;
  setEditDeductionAmount: (value: string) => void;
  handleEditDeductionAmountBlur: () => void;
  setEditDate: (value: LocalDate | null) => void;
  handleStartEdit: () => void;
  handleCancelEdit: () => void;
  setIsEditing: (value: boolean) => void;
}

interface UseShiftDetailsEditParams {
  detailsStartTime: string;
  detailsEndTime: string;
  breakStartTime?: string | null;
  breakEndTime?: string | null;
  deductionAmount?: number | null;
  totalTime?: string | null;
  isSaving: boolean;
}

export const useShiftDetailsEdit = (
  params: UseShiftDetailsEditParams,
): ShiftDetailsEditState => {
  const deductionAmountMaxDigits = 5;
  const [isEditing, setIsEditing] = useState(false);
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editBreakStartTime, setEditBreakStartTime] = useState("");
  const [editBreakEndTime, setEditBreakEndTime] = useState("");
  const [editDeductionAmount, setEditDeductionAmount] = useState("");
  const [editDate, setEditDate] = useState<LocalDate | null>(null);

  const initEditValues = useCallback((): void => {
    const dateValue = toStockholmLocalDateFromUtcIso(params.detailsStartTime);
    setEditStartTime(toTimeInputValue(params.detailsStartTime));
    setEditEndTime(toTimeInputValue(params.detailsEndTime));
    setEditBreakStartTime(toTimeInputValue(params.breakStartTime));
    setEditBreakEndTime(toTimeInputValue(params.breakEndTime));
    setEditDeductionAmount(
      normalizeNonNegativeDecimalNumber(params.deductionAmount),
    );
    setEditDate(dateValue);
  }, [
    params.breakEndTime,
    params.breakStartTime,
    params.deductionAmount,
    params.detailsEndTime,
    params.detailsStartTime,
  ]);

  /**
   * Derived date values from the current edit fields, including rollovers.
   */
  const editDerived = useMemo(() => {
    if (!editDate) return null;
    const startParts = parseTimeValue(editStartTime);
    const endParts = parseTimeValue(editEndTime);
    const breakStartParts = parseTimeValue(editBreakStartTime);
    const breakEndParts = parseTimeValue(editBreakEndTime);

    if (!startParts || !endParts || !breakStartParts || !breakEndParts) {
      return null;
    }

    const startDate = buildDateWithTimeFromLocalDate(editDate, startParts);
    let endDate = buildDateWithTimeFromLocalDate(editDate, endParts);
    const isShiftOvernight = shouldRollToNextDay(startParts, endParts);

    if (isShiftOvernight) {
      endDate = addStockholmDays(endDate, 1);
    }

    let breakStartDate = buildDateWithTimeFromLocalDate(
      editDate,
      breakStartParts,
    );

    let breakEndDate = buildDateWithTimeFromLocalDate(editDate, breakEndParts);

    if (shouldRollToNextDay(breakStartParts, breakEndParts)) {
      breakEndDate = addStockholmDays(breakEndDate, 1);
    }

    if (isShiftOvernight && breakStartDate < startDate) {
      breakStartDate = addStockholmDays(breakStartDate, 1);
      breakEndDate = addStockholmDays(breakEndDate, 1);
    }

    const breakMinutes = Math.max(
      0,
      Math.round((breakEndDate.getTime() - breakStartDate.getTime()) / 60000),
    );

    return {
      startDate,
      endDate,
      breakStartDate,
      breakEndDate,
      breakMinutes,
    };
  }, [
    editBreakEndTime,
    editBreakStartTime,
    editDate,
    editEndTime,
    editStartTime,
  ]);

  const editTotalTime = useMemo(() => {
    if (!editDerived) return params.totalTime ?? null;
    return (
      formatTotalTime(
        editDerived.startDate.toISOString(),
        editDerived.endDate.toISOString(),
        editDerived.breakMinutes,
      ) ?? null
    );
  }, [editDerived, params.totalTime]);

  const canSaveEdit =
    editDerived != null &&
    editStartTime.length > 0 &&
    editEndTime.length > 0 &&
    editBreakStartTime.length > 0 &&
    editBreakEndTime.length > 0 &&
    editDeductionAmount.length > 0 &&
    editDerived.breakStartDate >= editDerived.startDate &&
    editDerived.breakEndDate <= editDerived.endDate &&
    editDerived.breakEndDate >= editDerived.breakStartDate &&
    !params.isSaving;

  const handleSetEditDeductionAmount = (value: string): void => {
    setEditDeductionAmount(
      sanitizeNonNegativeDecimalInput(value, {
        maxDigits: deductionAmountMaxDigits,
      }),
    );
  };

  const handleEditDeductionAmountBlur = (): void => {
    setEditDeductionAmount((currentValue) =>
      coerceToNonNegativeDecimalString(currentValue),
    );
  };

  const handleStartEdit = (): void => {
    initEditValues();
    setIsEditing(true);
  };

  const handleCancelEdit = (): void => {
    initEditValues();
    setIsEditing(false);
  };

  return {
    isEditing,
    editStartTime,
    editEndTime,
    editBreakStartTime,
    editBreakEndTime,
    editDeductionAmount,
    editDate,
    editDerived,
    editTotalTime,
    canSaveEdit,
    setEditStartTime,
    setEditEndTime,
    setEditBreakStartTime,
    setEditBreakEndTime,
    setEditDeductionAmount: handleSetEditDeductionAmount,
    handleEditDeductionAmountBlur,
    setEditDate,
    handleStartEdit,
    handleCancelEdit,
    setIsEditing,
  };
};
